import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/http/client';

// Mirrors the backend SourceStatus.connection_status Literal in
// apps/meeting_notes_sync/services/sources_status.py
export type SourceConnectionStatus =
  | 'connected'
  | 'connected_no_delivery'
  | 'disconnected'
  | 'auth_failed';

export interface SourceStatus {
  connection_status: SourceConnectionStatus;
  last_received_at: string | null;
  meta: {
    email?: string;
    /**
     * Entity id of the connected `ahoy_external_integration` (Gmail account,
     * etc.). Present for Google Meet and Zoom when an integration is found.
     * Frontend renders this as an `EntityBadge` so the user can click through
     * to the integration record.
     */
    integration_entity_id?: string;
  };
  error?: 'fetch_failed';
}

/**
 * Whether a source's connection is established, regardless of delivery health.
 *
 * `connected_no_delivery` counts as connected: the integration row exists and
 * auth is fine, summaries just aren't landing. Callers use this for
 * "connection exists" decisions (show Disconnect, show connection meta);
 * delivery-health rendering branches on the specific status separately.
 */
export function isSourceConnected(status: SourceStatus): boolean {
  return (
    status.connection_status === 'connected' ||
    status.connection_status === 'connected_no_delivery'
  );
}

export type SourceKey =
  | 'fathom'
  | 'google_meet'
  | 'microsoft_teams'
  | 'zoom'
  | 'granola'
  | 'fireflies'
  | 'ahoy_desktop';

export type SourcesStatusResponse = Record<SourceKey, SourceStatus>;

export function useMeetingSummariesSourcesStatus(orgId: number | undefined) {
  return useQuery<SourcesStatusResponse>({
    queryKey: ['meetingSummariesSourcesStatus', orgId],
    queryFn: () =>
      apiGet<SourcesStatusResponse>(
        `/api/orgs/${orgId}/integrations/meeting-notes/sources/status/`
      ),
    enabled: !!orgId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
